import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Mic, RefreshCcw, AlertCircle } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
};

/**
 * Lite invite landing — generic, no org branding.
 * URL: /lite/<code>  e.g. /lite/sap2026
 *
 * Validates the code against the backend, stores it in sessionStorage so the
 * OAuth callback can attach it as X-Org-Code on /api/auth/session-data, and
 * funnels the user into Emergent OAuth.
 */
const LandingLite = () => {
  const { code } = useParams();
  const [invite, setInvite] = useState({ loading: true, valid: false, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let result;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/lite/invite/${encodeURIComponent(code)}`
        );
        result = res.ok
          ? { loading: false, valid: true, error: null }
          : { loading: false, valid: false, error: 'This invite link is no longer active.' };
      } catch (e) {
        result = { loading: false, valid: false, error: 'Could not reach the server. Please try again.' };
      }
      if (cancelled) return;
      if (result.valid) {
        sessionStorage.setItem('pending_org_code', code);
      }
      setInvite(result);
    })();
    return () => { cancelled = true; };
  }, [code]);

  const beginAuth = () => {
    const REDIRECT_URL = encodeURIComponent(window.location.origin + '/dashboard');
    const AUTH_URL = `${process.env.REACT_APP_AUTH_URL}/?redirect=${REDIRECT_URL}`;
    window.location.href = AUTH_URL;
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink overflow-x-hidden">
      {/* Soft hero glow */}
      <div className="bg-stripe-glow">
        {/* Top bar */}
        <header className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pt-8 sm:pt-10 flex items-center justify-between">
          <div className="flex items-center" data-testid="lite-home-logo">
            <img src="/logo.png" alt="ReflectiveMind" className="h-10 sm:h-12 w-auto" />
          </div>
        </header>

        {/* Hero */}
        <section className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-12 sm:pb-20 text-center">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-accent/10 text-brand-accent text-xs font-semibold uppercase tracking-wider mb-6"
          >
            <Sparkles className="h-3.5 w-3.5" />
            You&apos;re invited
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
          >
            Quick reflective loops.
            <br />
            <span className="bg-gradient-to-r from-brand-accent to-[#5EAEFF] bg-clip-text text-transparent">
              One weekly insight.
            </span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-lg sm:text-xl text-brand-ink-muted leading-relaxed max-w-xl mx-auto"
          >
            Capture short reflections — by keyboard or voice. We tag and sense the mood,
            then build you a single AI summary whenever you want one. No noise. No overwhelm.
            Just the small ritual that helps you grow on purpose.
          </motion.p>

          {/* CTA panel */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10"
          >
            {invite.loading && (
              <div className="inline-flex items-center gap-2 text-brand-ink-muted text-sm" data-testid="lite-loading">
                <RefreshCcw className="h-4 w-4 animate-spin" /> Checking your invite…
              </div>
            )}

            {!invite.loading && invite.valid && (
              <button
                onClick={beginAuth}
                data-testid="lite-begin-btn"
                className="inline-flex items-center gap-2 bg-brand-accent hover:bg-brand-accent-hover text-white px-7 py-3.5 rounded-full font-semibold shadow-[0_8px_24px_rgba(5,112,222,0.35)] hover:-translate-y-0.5 transition-all"
              >
                Begin reflecting
                <ArrowRight className="h-4 w-4" />
              </button>
            )}

            {!invite.loading && !invite.valid && (
              <div
                className="inline-flex flex-col items-center gap-3 text-brand-ink-muted"
                data-testid="lite-invalid"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-brand-border bg-white text-sm">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  {invite.error || 'This invite link is no longer active.'}
                </div>
                <p className="text-xs text-brand-ink-muted max-w-sm text-center">
                  Please check the link from your training materials, or reach
                  out to whoever invited you.
                </p>
              </div>
            )}

            <p className="mt-5 text-xs text-brand-ink-muted">
              Free for invited members · By invitation only · Sign in with Google
            </p>
          </motion.div>
        </section>

        {/* Three-feature strip */}
        <section className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 pb-20 sm:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Feature
              icon={Mic}
              title="Capture in seconds"
              body="Type a sentence or hold the mic for a 60-second voice note. We transcribe and tag automatically."
            />
            <Feature
              icon={Sparkles}
              title="Smart tags + sentiment"
              body="Every reflection is tagged by theme and tone, so a week from now you can see the patterns at a glance."
            />
            <Feature
              icon={RefreshCcw}
              title="On-demand weekly summary"
              body="Tap once to generate a focused weekly summary — wins, blockers, themes — right inside the app. Review every past one in one place."
            />
          </div>
        </section>

        {/* Footer */}
        <footer className="relative z-10 border-t border-brand-border bg-white/60 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-brand-ink-muted">
            <span>© {new Date().getFullYear()} ReflectiveMind. Reflect, learn, grow on purpose.</span>
            <div className="flex items-center gap-5">
              <Link to="/privacy" className="hover:text-brand-accent">Privacy</Link>
              <Link to="/terms" className="hover:text-brand-accent">Terms</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

const Feature = ({ icon: Icon, title, body }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.5 }}
    className="bg-white border border-brand-border rounded-2xl p-6 hover:border-brand-accent/30 hover:shadow-[0_8px_24px_rgba(5,112,222,0.08)] transition-all"
  >
    <div className="w-10 h-10 rounded-xl bg-brand-accent/10 flex items-center justify-center mb-4">
      <Icon className="h-5 w-5 text-brand-accent" />
    </div>
    <h3 className="font-semibold text-brand-ink mb-1.5">{title}</h3>
    <p className="text-sm text-brand-ink-muted leading-relaxed">{body}</p>
  </motion.div>
);

export default LandingLite;
